

















import { computed, defineComponent, ref } from "@vue/composition-api";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { QuestionaryInfo } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { InputUserUpdate } from "@scrile/api-provider/dist/api/UserProvider";
import {
  InputUserPublicInformationUpdate,
  UserPublicInformation,
} from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { updateHasLocation } from "@/hooks/useGeolocation";
import useTags from "@/hooks/useTags";
import useUserVerification from "@/hooks/useUserVerification";
import Store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormValues } from "@/types";
import { ProfileFileType } from "@/const";
import PageProfileEditView from "./components/PageProfileEditView.vue";

const userFieldNamesByProfileFileType: Record<ProfileFileType, Partial<keyof UserPublicData>> = {
  [ProfileFileType.AVATAR]: "avatar",
  [ProfileFileType.AVATAR_NSFW]: "avatarNsfw",
  [ProfileFileType.BACKGROUND]: "background",
  [ProfileFileType.BACKGROUND_NSFW]: "backgroundNsfw",
};

export default defineComponent({
  name: "PageProfileEdit",
  components: { PageProfileEditView },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const { authUserStrict } = useAuthUser();
    const { getTags, tags } = useTags([TagInfoType.USERS, TagInfoType.CONTENT_TYPE]);
    const { findMyStatus } = useUserVerification();

    const loading = ref(true);
    const saving = ref(false);
    const info = ref<Record<string, string>>({});
    const user = ref<UserPublicData>();
    const publicInformation = ref<UserPublicInformation>();
    const userTags = ref<string[]>([]);
    const userContentTypes = ref<string[]>([]);

    const isAuthor = computed(() => authUserStrict.value.id === props.userId);

    const selectedTags = computed<TagInfo[]>(() => tags.value.filter(t => userTags.value.includes(t.id)));
    const selectedContentTypes = computed<TagInfo[]>(() =>
      tags.value.filter(t => userContentTypes.value.includes(t.id))
    );

    const getUserData = async () => {
      const r = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      user.value = r.result.find(i => i.id === props.userId);
    };

    const getSystemTags = async () => {
      if (!config.featureTags) return;
      await getTags();
    };

    const getQuestionaryInfo = async () => {
      const response = await providers.QuestionaryInfoProvider.find({ id: props.userId });
      const { tags, tagsContentType, ...fields } = response.fields;
      info.value = fields as Record<string, string>;
      userTags.value = (tags as number[]).map(t => t.toString());
      userContentTypes.value = (tagsContentType as number[]).map(t => t.toString());
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([
          getQuestionaryInfo(),
          providers.UserPublicDataProvider.userPublicInformationFind(props.userId).then(
            r => (publicInformation.value = r)
          ),
          getUserData(),
          getSystemTags(),
          findMyStatus(),
        ]);
      } finally {
        loading.value = false;
      }
    };

    const updateFiles = (type: ProfileFileType, file: FileData | null) => {
      if (!user.value) return;
      user.value = { ...user.value, [userFieldNamesByProfileFileType[type]]: file };
    };

    const updateUserTags = (tags: string[]) => {
      userTags.value = tags;
    };

    const updateUserContentTypes = (tags: string[]) => {
      userContentTypes.value = tags;
    };

    const saveQuestionaryInfo = async (values: Record<string, string>, cb?: () => void) => {
      try {
        getQuestionaryInfoFields().forEach(field => {
          if (!values[field.name]) return;

          const trimmedValue = values[field.name].trim();
          if (trimmedValue && field.fieldMeta?.regExp) {
            if (!field.fieldMeta.appendText) {
              const execResult: RegExpExecArray = field.fieldMeta.regExp.exec(trimmedValue);
              if (execResult[1]) values[field.name] = execResult[1];
            } else if (!field.fieldMeta.regExp.test(trimmedValue)) {
              values[field.name] = `${field.fieldMeta.appendText}${trimmedValue}`;
            }
            return;
          }
          values[field.name] = trimmedValue;
        });

        const questionaryInfo: QuestionaryInfo = {
          fields: {
            ...info.value,
            ...values,
            userId: props.userId,
          },
        };

        const response = await providers.QuestionaryInfoProvider.update({ data: questionaryInfo });
        info.value = response.fields as Record<string, string>;
      } finally {
        cb && cb();
      }
    };

    const save = async (values: FormValues) => {
      const { screenName, username, gender, publicInfoValues, ...questionaryInfoFields } = values;
      const userData: InputUserUpdate = {
        id: props.userId,
        screenName,
        username,
        gender,
      };

      const userPublicInformation: InputUserPublicInformationUpdate = {
        userId: props.userId,
        ...publicInfoValues,
      };

      const promises: Promise<any>[] = [
        saveQuestionaryInfo(questionaryInfoFields),
        providers.UserProvider.update({ data: userData }).then(
          r => isAuthor.value && Store.commit(MUTATION_TYPES.SET_USER, r)
        ),
        providers.UserPublicDataProvider.userPublicInformationUpdate(userPublicInformation).then(
          r => isAuthor.value && updateHasLocation(r, root)
        ),
      ];
      try {
        saving.value = true;
        await Promise.all(promises);
      } finally {
        saving.value = false;
      }
      root.$router.push(getProfileUrl(props.userId));
    };

    getData();

    return {
      loading,
      saving,
      user,
      info,
      selectedTags,
      selectedContentTypes,
      publicInformation,
      updateUserTags,
      updateUserContentTypes,
      save,
      saveQuestionaryInfo,
      updateFiles,
    };
  },
});
